<template>
  <div class="interests margin-t-2">
    <div class="dashboard-page-title">
      <div class="container-fluid" ref="width">
        <router-link :to="{ name: 'talent.profile.show', params: { id: this.userId } }" class="btn btn-info">
          <i class="fa fa-chevron-left"></i> Back
        </router-link>
      </div>
    </div>

    <div class="container-fluid">
      <div class="dashboard-block">
        <ul class="nav nav-tabs" role="tablist">
          <li :class="{ 'active': page === 'interests' }">
            <router-link :to="{ params: { page: 'interests' }, query: $route.query }">Interests</router-link>
          </li>
          <li :class="{ 'active': page === 'specialist' }">
            <router-link :to="{ params: { page: 'specialist' }, query: $route.query }">Specialist Topics</router-link>
          </li>
          <li :class="{ 'active': page === 'personal' }">
            <router-link :to="{ params: { page: 'personal' }, query: $route.query }">Hobbies</router-link>
          </li>
        </ul>
        <profile-interest-categories
          v-show="page === 'interests'"
          :interest-categories="interestCategories"
          :custom-categories="customCategories"
          :selected-categories="selectedCategories"
          @toggled="toggleInterestCategory"
          @selected="(option) => { selectInterestCategory(option, 'interest') }"
          @deleted="deleteInterestCategory"/>
        <profile-specialist-categories
          v-show="page === 'specialist'"
          :interest-categories="interestCategories"
          :custom-categories="customCategories"
          :selected-categories="selectedCategories"
          @toggled="toggleSpecialistCategory"
          @selected="(option) => { selectInterestCategory(option, 'specialist') }"/>
        <profile-personal-interests
          v-show="page === 'personal'"
          :personal-interests="personalInterests"
          :suggested-personal-interests="suggestedPersonalInterests"
          @unselect="unselectPersonalInterest"
          @selected="selectPersonalInterest"/>
      </div>
    </div>
    <div class="container-fluid" :class="fixed ? 'fixed' : 'relative'" style="width: 100%;" :style="'max-width: ' + width + 'px;' + 'min-width: ' + width + 'px'">
      <div class="flex footer">
        <div class="hidden-xs">
          {{ footerContent.content }}
        </div>
        <div class="flex flex-row align-center">
          <div class="spacer-r-1 hidden-xs">{{ footerContent.count }} Selected</div>
          <router-link :to="footerContent.link" class="btn btn-primary">
            {{ footerContent.button }}
            <i v-if="footerContent.icon" class="spacer-l-1 fa" :class="footerContent.icon"></i>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from 'utils'
import { mapState } from 'vuex'
import ProfileInterestCategories from './ProfileInterestCategories'
import ProfileSpecialistCategories from './ProfileSpecialistCategories'
import ProfilePersonalInterests from './ProfilePersonalInterests'
import { OpportunityTracker } from 'mixins/analytics'
export default {
  mixins: [OpportunityTracker],

  components: { ProfilePersonalInterests, ProfileSpecialistCategories, ProfileInterestCategories },

  props: {
    page: {
      required: false,
      default: 'interests'
    }
  },

  watch: {
    page () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },

  data () {
    return {
      interestCategories: [],
      customCategories: [],
      personalInterests: [],
      suggestions: [],
      fixed: true,
      width: 1200
    }
  },

  mounted () {
    http.get(`interest-categories/${this.userId}/manage`).then(response => {
      this.interestCategories = response.data
      if (response.data['']) {
        this.customCategories = response.data['']
        delete this.interestCategories['']
      }
    })
    http.get(`personal-interests/${this.userId}/selected`).then(response => {
      this.personalInterests = response.data
    })
    http.get(`personal-interests/suggestions`).then(response => {
      this.suggestions = response.data
    })

    const target = document.getElementById('footer')
    const options = {
      root: null,
      threshold: 0
    }
    const callback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.fixed = false
        } else {
          this.fixed = true
        }
      })
    }
    // eslint-disable-next-line
    const observer = new IntersectionObserver(callback, options)
    observer.observe(target)
    window.addEventListener('resize', this.handleResize)
  },

  updated () {
    this.handleResize()
  },

  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    handleResize () {
      if (this.$refs.width) {
        this.width = this.$refs.width.clientWidth ? this.$refs.width.clientWidth : 1200
      }
    },

    toggleInterestCategory (id, index, group, name) {
      this.interestCategories[group][index].selected = !this.interestCategories[group][index].selected
      const action = this.interestCategories[group][index].selected ? 'select' : 'deselect'
      http.put(`interest-categories/${this.userId}/${id}/${action}`)
      if (action === 'select') {
        this.trackEvent(this.analytics.V2.AccountTaggingElementAppliedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.INTERESTS, tagName: name })
      } else {
        this.trackEvent(this.analytics.V2.AccountTaggingElementRemovedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.INTERESTS, tagName: name })
      }
    },

    toggleSpecialistCategory (id, group, name) {
      let type = null
      if (group) {
        const index = this.interestCategories[group].findIndex(element => element.id === id)
        if (index !== -1) {
          type = this.interestCategories[group][index].type === 'specialist' ? 'interest' : 'specialist'
          this.interestCategories[group][index].type = type
          http.put(`interest-categories/${this.userId}/${id}/select`, { type })
        }
      } else {
        const index = this.customCategories.findIndex(element => element.id === id)
        if (index !== -1) {
          type = this.customCategories[index].type === 'specialist' ? 'interest' : 'specialist'
          this.customCategories[index].type = type
          http.put(`interest-categories/${this.userId}/${id}/select`, { type })
        }
      }

      if (type === 'specialist') {
        this.trackEvent(this.analytics.V2.AccountTaggingElementAppliedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.SPECIALIST_TOPICS, tagName: name })
      } else {
        this.trackEvent(this.analytics.V2.AccountTaggingElementRemovedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.SPECIALIST_TOPICS, tagName: name })
      }
    },

    selectInterestCategory (option, type = 'interest') {
      if (option.group) {
        const group = option.group
        const id = option.id
        const index = this.interestCategories[group].findIndex(element => element.id === id)
        if (index !== -1) {
          this.interestCategories[group][index].selected = true
          this.interestCategories[group][index].type = type
          http.put(`interest-categories/${this.userId}/${id}/select`, { type })
        }
      } else {
        const customId = option.id
        http.post(`interest-categories/${this.userId}/create`, { name: option.name, type }).then(response => {
          const index = this.customCategories.findIndex(element => element.id === customId)
          if (index !== -1) {
            this.customCategories.splice(index, 1, response.data)
          }
        })
        option.type = type
        this.customCategories.push(option)
      }
      this.trackEvent(this.analytics.V2.AccountTaggingElementAppliedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.INTERESTS, tagName: option.name })
    },

    deleteInterestCategory (id, index, name) {
      this.customCategories.splice(index, 1)
      http.delete(`interest-categories/${id}/delete`)
      this.trackEvent(this.analytics.V2.AccountTaggingElementRemovedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.INTERESTS, tagName: name })
    },

    selectPersonalInterest (option) {
      if (!isNaN(option.id)) {
        http.put(`personal-interests/${this.userId}/${option.id}/select`)
      } else {
        const customId = option.id
        http.post(`personal-interests/${this.userId}/create`, { name: option.name }).then(response => {
          const index = this.personalInterests.findIndex(element => element.id === customId)
          if (index !== -1) {
            this.personalInterests.splice(index, 1, response.data)
          }
        })
      }
      this.personalInterests.push(option)
      this.trackEvent(this.analytics.V2.AccountTaggingElementAppliedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.HOBBIES, tagName: option.name })
    },

    unselectPersonalInterest (id, index, name) {
      this.personalInterests.splice(index, 1)
      http.put(`personal-interests/${this.userId}/${id}/deselect`)
      this.trackEvent(this.analytics.V2.AccountTaggingElementRemovedEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.HOBBIES, tagName: name })
    }
  },

  computed: {
    ...mapState('user', ['user']),

    userId () {
      return this.$route.params && this.$route.params.id ? this.$route.params.id : this.$user.id
    },

    selectedCategories () {
      const selectedCategories = []
      Object.keys(this.interestCategories).forEach(key => {
        selectedCategories.push(...this.interestCategories[key].filter(category => category.selected))
      })
      return selectedCategories
    },

    selectedSpecialistTopicsCount () {
      return this.selectedCategories.filter(element => element.type === 'specialist').length + this.customCategories.filter(element => element.type === 'specialist').length
    },

    suggestedPersonalInterests () {
      const selectedIds = this.personalInterests.map(element => element.id)
      return this.suggestions.filter(element => !selectedIds.includes(element.id))
    },

    footerContent () {
      switch (this.page) {
        case 'interests':
          return {
            content: 'We recommend selecting 5 or more interests',
            count: this.selectedCategories.length,
            button: 'Select specialist topics',
            icon: 'fa-chevron-right',
            link: { params: { page: 'specialist' }, query: this.$route.query }
          }
        case 'specialist':
          return {
            content: 'Select all the topics which apply',
            count: this.selectedSpecialistTopicsCount,
            button: 'Select hobbies',
            icon: 'fa-chevron-right',
            link: { params: { page: 'personal' }, query: this.$route.query }
          }
        case 'personal':
          return {
            content: 'We recommend selecting 3 or more hobbies',
            count: this.personalInterests.length,
            button: 'Confirm',
            icon: null,
            link: { name: 'talent.profile.show', params: { id: this.userId } },
            exit: true
          }
        default:
          return {}
      }
    }
  }
}
</script>

<style lang="scss">
.interests {
  .add-button {
    color: var(--brand-primary);
    text-transform: uppercase;
    letter-spacing: 2px;
    background: none;
    border: none;
    font-weight: 700;
  }
  .fixed {
    position: fixed;
    bottom: 0;
  }
  .relative {
    position: absolute;
    bottom: 0;
  }
  .footer {
    background-color: white;
    align-items: center;
    padding: 20px;
    @media(min-width: 576px) {
      justify-content: space-between;
    }
    @media(max-width: 576px) {
      justify-content: center;
    }
  }
  .info-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    @media(max-width: 576px) {
      width: 100%;
    }
    @media(min-width: 576px) {
      width: 50%;
    }
  }
  .lead-text {
    font-weight: bold;
    font-size: 14px;
    font-family: Open Sans;
  }
  .category {
    background: black;
    color: white;
    padding: 4px 12px;
    border: 2px solid black;
    font-size: 12px;
    font-family: Open Sans,Sans-serif;
    letter-spacing: 1px;
    font-weight: 700;
    cursor: pointer;
    margin-right: 4px;
    margin-top: 4px;
    i {
      width: 13px;
    }
    &--selected {
      background: var(--brand-primary);
      border-color: var(--brand-primary);
    }
  }
}
</style>
